import { Component } from "@theme/component";
import { CartAddEvent } from "@theme/events";
import {
  BUNDLE_FLOW_UPDATE_EVENT,
  NUGGET_ONE_TIME_VARIANT,
  addNuggetOneTimeToCart,
  bundleState,
} from "@theme/nugget-bundle";

const SCROLL_THRESHOLD = 56;

class NuggetBundleBanner extends Component {
  #lastScrollY = 0;
  #isActive = false;

  connectedCallback() {
    super.connectedCallback();
    
    // Check initial bundle flow state
    if (bundleState.bundleFlow) {
      this.#isActive = true;
      this.#lastScrollY = window.scrollY;
      this.dataset.scrollDirection = "none";
      this.classList.remove("hidden");
      this.classList.add("flex");
    }
    
    document.addEventListener(
      BUNDLE_FLOW_UPDATE_EVENT,
      this.#handleBundleFlowChange,
    );

    // Fallback: allow other components to request the cancellation modal.
    // This makes the flow work even when no nugget product card exists.
    document.addEventListener(
      "bundle:show-cancellation",
      this.#handleShowCancellation,
    );

    document.addEventListener("click", this.#handleCancellationModalClick);
    window.addEventListener("scroll", this.#handleScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      BUNDLE_FLOW_UPDATE_EVENT,
      this.#handleBundleFlowChange,
    );
    document.removeEventListener(
      "bundle:show-cancellation",
      this.#handleShowCancellation,
    );

    document.removeEventListener("click", this.#handleCancellationModalClick);
    window.removeEventListener("scroll", this.#handleScroll);
  }

  #handleScroll = () => {
    if (!this.#isActive) return;

    const currentScrollY = window.scrollY;
    const scrollingDown =
      currentScrollY > this.#lastScrollY && currentScrollY > SCROLL_THRESHOLD;

    this.dataset.scrollDirection = scrollingDown ? "down" : "none";
    this.#lastScrollY = currentScrollY;
  };

  #handleBundleFlowChange = (event) => {
    const { active } = event.detail ?? {};
    if (typeof active !== "boolean") return;

    if (active) {
      this.#isActive = true;
      this.#lastScrollY = window.scrollY;
      this.dataset.scrollDirection = "none";
      this.classList.remove("hidden");
      this.classList.add("flex");
    } else {
      this.#isActive = false;
      this.classList.add("hidden");
      this.classList.remove("flex");
    }
  };

  handleClose() {
    this.openCancellationModal();
  }

  openCancellationModal() {
    this.#getCancellationModal()?.showDialog();
    requestAnimationFrame(() => {
      window.grAnalytics?.trackBundleWarningModalViewed({});
    });
  }

  closeCancellationModal() {
    return this.#getCancellationModal()?.closeDialog() ?? Promise.resolve();
  }

  async startBundleFlow() {
    await this.closeCancellationModal();
    bundleState.setBundleFlow(true, this.id);

    window.grAnalytics?.trackBundleFlowEntry({});

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  async addOneTimeToCart() {
    await this.closeCancellationModal();

    if (bundleState.bundleFlow) {
      bundleState.setBundleFlow(false, "one-time-checkout");
    }

    try {
      await addNuggetOneTimeToCart();

      this.dispatchEvent(
        new CartAddEvent({}, this.id || "nugget-bundle-banner", {
          source: "nugget-bundle-banner",
          variantId: String(NUGGET_ONE_TIME_VARIANT),
          itemCount: 1,
        }),
      );
    } catch (error) {
      console.error("Failed to add nugget to cart:", error);
    }
  }

  #handleShowCancellation = () => {
    this.openCancellationModal();
  };

  #handleCancellationModalClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const actionEl = target?.closest?.("[data-nugget-bundle-cancel-action]");
    if (!(actionEl instanceof HTMLElement)) return;

    const action = actionEl.getAttribute("data-nugget-bundle-cancel-action");
    if (!action) return;

    if (action === "startBundle") {
      event.preventDefault();
      this.startBundleFlow();
    }

    if (action === "oneTime") {
      const label = actionEl.getAttribute("aria-label") || "";
      if (label.toLowerCase().includes("close")) {
        window.grAnalytics?.trackBundleWarningDismissed({
          action: "close",
        });
      } else if (label.toLowerCase().includes("continue")) {
        window.grAnalytics?.trackBundleContinueWithoutSavings({});
      }
      event.preventDefault();
      this.addOneTimeToCart();
    }
  };

  #getCancellationModal() {
    return /** @type {import('@theme/dialog').DialogComponent | null} */ (
      document.querySelector("[data-nugget-bundle-cancellation-modal]")
    );
  }
}

if (!customElements.get("nugget-bundle-banner")) {
  customElements.define("nugget-bundle-banner", NuggetBundleBanner);
}

export { NuggetBundleBanner };
